import { t as __t } from "i18next";
import { QRCodeCanvas } from "qrcode.react";
import React, { useEffect, useMemo, useState } from "react";

import {
    convertStringToJson,
    displayUrl,
} from "../../../utils/commonUtil";

import { APPROVE } from "../../../utils/commonType";
import PermissionWrapper from "../../../components/common/PermissionWrapper";

import "./payment-compact.css";
import { openDocumentPreview } from "../../../components/document-preview";

const isShown = (value) => {
    if (value === null || value === undefined || value === "") {
        return false;
    }

    const numberValue = Number(value);
    return !Number.isNaN(numberValue) && numberValue !== 0;
};

const safeParseFiles = (value) => {
    if (Array.isArray(value)) return value;
    if (!value) return [];

    try {
        const parsed = convertStringToJson(value);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        return [];
    }
};

export default function PaymentPage({
    qrCode,
    t,
    FormatCurrencys,
    onPay,
    isDebit = false,
    countdownSeconds = 60,
    compact = false,
}) {
    const currency = t("currentcy.kip");
    const [countdown, setCountdown] = useState(countdownSeconds);

    const isPaid = Number(qrCode?.status) === 1 || Number(qrCode?.status) === 3;
    const hasQrCode = Boolean(qrCode?.value);

    useEffect(() => {
        console.log("qrCode?.transactionId", qrCode);
        if (!hasQrCode || isPaid) {
            setCountdown(countdownSeconds);
            return undefined;
        }

        setCountdown(countdownSeconds);

        const timer = window.setInterval(() => {
            setCountdown((previous) => {
                if (previous <= 1) {
                    window.clearInterval(timer);
                    return 0;
                }

                return previous - 1;
            });
        }, 1000);

        return () => window.clearInterval(timer);
    }, [
        hasQrCode,
        isPaid,
        countdownSeconds,
        qrCode?.transactionId,
    ]);

    const formatCountdown = (seconds) => {
        const totalSeconds = Math.max(0, Number(seconds || 0));
        const minutes = Math.floor(totalSeconds / 60);
        const remainingSeconds = totalSeconds % 60;

        return `${String(minutes).padStart(2, "0")}:${String(
            remainingSeconds
        ).padStart(2, "0")}`;
    };

    const items = useMemo(() => {
        if (!qrCode) return [];

        const config = [
            {
                key: "payPerMonth",
                label: t("contracts.vehicle.payPerMonth"),
                type: "add",
            },
            {
                key: "vipAmount",
                label: t("contracts.vehicle.vipAmount"),
                type: "add",
            },
            {
                key: "vipAmount0",
                label: t("contracts.vehicle.vipAmount"),
                type: "add",
            },
            {
                key: "vipAmount1",
                label: t("contracts.vehicle.vipAmount"),
                type: "add",
            },
            {
                key: "discount",
                label: t("contracts.vehicle.discount"),
                type: "minus",
            },
            {
                key: "priceAfterDiscount",
                label: t(
                    "contracts.vehicle.priceAfterDiscount"
                ),
                type: "neutral",
            },
            {
                key: "civelLiability",
                label: t("contracts.vehicle.civelLiability"),
                type: "add",
            },
            {
                key: "guaranteenFee",
                label: t("contracts.vehicle.guaranteenFee"),
                type: "add",
            },
            {
                key: "guaranteenFee0",
                label: t("contracts.vehicle.guaranteenFee"),
                type: "add",
            },
            {
                key: "guaranteenFee1",
                label: t("contracts.vehicle.guaranteenFee"),
                type: "add",
            },
            {
                key: "guaranteenFee2",
                label: t("contracts.vehicle.guaranteenFee"),
                type: "add",
            },
            {
                key: "sumTotal",
                label: t("contracts.vehicle.sumTotal"),
                type: "neutral",
            },
            {
                key: "repairCost",
                label: t("contracts.vehicle.repairCost"),
                type: "add",
            },
            {
                key: "vatValue",
                label: t("contracts.vehicle.vatValue"),
                type: "add",
            },
            {
                key: "RVPEAmount",
                label: t("contracts.vehicle.RVPEAmount"),
                type: "add",
            },
            {
                key: "feeRegister",
                label: t("contracts.vehicle.feeRegister"),
                type: "add",
            },
            {
                key: "total",
                label: t("contracts.vehicle.total"),
                type: "neutral",
            },
            {
                key: "commission",
                label: t("contracts.vehicle.commission"),
                type: "minus",
                percent: "commissionRate",
                percent3D: "comrate3D",
            },
            {
                key: "paymentFee",
                label: t("contracts.vehicle.paymentFee"),
                type: "add",
            },
            {
                key: "companyReceiveTotal",
                label: t(
                    "contracts.vehicle.companyReceiveTotal"
                ),
                type: "neutral",
                isTotal: true,
            },
        ];

        return config
            .map((item) => {
                let percentText = "";

                if (item.percent && item.percent3D) {
                    const rate = Number(
                        qrCode?.[item.percent] || 0
                    );
                    const rate3D = Number(
                        qrCode?.[item.percent3D] || 0
                    );

                    percentText = `(${rate - rate3D} + ${rate3D}%)`;
                } else if (item.percent) {
                    percentText = `(${Number(
                        qrCode?.[item.percent] || 0
                    )}%)`;
                }

                return {
                    ...item,
                    value: qrCode?.[item.key],
                    percentText,
                };
            })
            .filter((item) => isShown(item.value));
    }, [qrCode, t]);

    const totalPayable = Number(
        qrCode?.companyReceiveTotal ??
        qrCode?.total ??
        0
    );

    const billFile = useMemo(() => {
        return safeParseFiles(qrCode?.billPath)?.[0] || null;
    }, [qrCode?.billPath]);

    const openFile = (event) => {
        event.preventDefault();
        if (billFile) openDocumentPreview(billFile);

    };

    const renderValue = (item) => {
        const numberValue = Number(item.value || 0);
        const formattedValue = `${FormatCurrencys(
            Math.abs(numberValue)
        )} ${currency}`;

        if (item.type === "minus") {
            return (
                <span className="pc-minus">
                    -{formattedValue}
                </span>
            );
        }

        if (item.isTotal) {
            return (
                <span className="pc-total-value">
                    {formattedValue}
                </span>
            );
        }

        if (item.type === "add") {
            return (
                <span className="pc-add">
                    +{formattedValue}
                </span>
            );
        }

        return (
            <span className="pc-normal">
                {formattedValue}
            </span>
        );
    };

    return (
        <div
            className={[
                "pc-wrapper",
                compact ? "pc-compact" : "",
                isPaid ? "pc-paid" : "",
            ]
                .filter(Boolean)
                .join(" ")}
        >
            <div className="pc-header">
                <div className="pc-header-left">
                    <div className="pc-header-icon">
                        <i className="pi pi-wallet"></i>
                    </div>

                    <div className="pc-header-text">
                        <small>
                            {t("payment.title") || "Payment"}
                        </small>
                        <h4>
                            {t("payment.checkout") || "Checkout"}
                        </h4>
                        <span>
                            {t("payment.ref") || "Reference"}:{" "}
                            {qrCode?.transactionId || "-"}
                        </span>
                    </div>
                </div>

                <span
                    className={
                        isPaid && Number(qrCode?.status) === 1
                            ? "pc-status-success"
                            : "pc-status-warning"
                    }
                >
                    <i
                        className={
                            isPaid && Number(qrCode?.status) === 1
                                ? "pi pi-check-circle"
                                : "pi pi-clock"
                        }
                    ></i>
                    {isPaid && Number(qrCode?.status) === 1
                        ? t("payment.success") || "Success"
                        : t("payment.warning") || "Pending"}
                </span>
            </div>

            <div
                className={`pc-grid ${isPaid ? "pc-grid-single" : ""
                    }`}
            >
                <div className="pc-panel pc-breakdown-panel">
                    <div className="pc-panel-title">
                        <div>
                            <i className="pi pi-list"></i>
                            <h5>
                                {t("payment.breakdown") ||
                                    "Payment breakdown"}
                            </h5>
                        </div>
                        <span>{currency}</span>
                    </div>

                    <div className="pc-breakdown-list">
                        {items.length > 0 ? (
                            items.map((item) => (
                                <div
                                    className={`pc-breakdown-item ${item.isTotal
                                        ? "pc-breakdown-highlight"
                                        : ""
                                        }`}
                                    key={item.key}
                                >
                                    <div className="pc-label">
                                        <span>{item.label}</span>

                                        {item.percentText && (
                                            <small>
                                                {item.percentText}
                                            </small>
                                        )}
                                    </div>

                                    <div className="pc-value">
                                        {renderValue(item)}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="pc-empty">
                                <i className="pi pi-inbox"></i>
                                <span>
                                    {__t("uiText.text257")}</span>
                            </div>
                        )}
                    </div>

                    <div className="pc-footer">
                        {/* <div className="pc-total-box">
                            <div>
                                <small>
                                    {t("payment.totalPayable") ||
                                        "Total payable"}
                                </small>

                                <strong>
                                    {FormatCurrencys(
                                        totalPayable
                                    )}
                                    <span>{currency}</span>
                                </strong>
                            </div>

                            <i className="pi pi-money-bill"></i>
                        </div> */}

                        <div className="pc-actions">
                            <PermissionWrapper
                                permission={APPROVE}
                            >
                                <button
                                    type="button"
                                    disabled={
                                        (isPaid && Number(qrCode?.status) === 1) || qrCode?.statusContract == 0
                                    }
                                    className="pc-button pc-button-primary"
                                    onClick={() =>
                                        onPay?.({
                                            ...qrCode,
                                            inDebt: false,
                                        })
                                    }
                                >
                                    <i className="pi pi-check"></i>
                                    {t("payments.payNow") ||
                                        "Confirm payment"}
                                </button>
                            </PermissionWrapper>

                            <button
                                type="button"
                                disabled={!billFile}
                                className="pc-button pc-button-outline"
                                onClick={openFile}
                            >
                                <i className="pi pi-file-pdf"></i>
                                {t(
                                    "payments.downloadInvoice"
                                ) || "View invoice"}
                            </button>
                        </div>
                    </div>
                </div>

                {!isPaid && (
                    <div className="pc-panel pc-method-panel">
                        <div className="pc-panel-title">
                            <div>
                                <i className="pi pi-credit-card"></i>
                                <h5>
                                    {t("payment.method") ||
                                        "Payment method"}
                                </h5>
                            </div>
                        </div>

                        <div className="pc-method-content">
                            <div className="pc-method-list">
                                <button
                                    type="button"
                                    className="pc-method-button"
                                    onClick={() =>
                                        onPay?.({
                                            ...qrCode,
                                            inDebt: false,
                                            paymentType: "bank",
                                        })
                                    }
                                >
                                    <div className="pc-method-icon">
                                        <i className="pi pi-building-columns"></i>
                                    </div>

                                    <div>
                                        <strong>
                                            {t(
                                                "payment.bankTransfer"
                                            ) || "Bank transfer"}
                                        </strong>
                                        <span>
                                            {t("payment.bcelone") ||
                                                "Pay through bank application"}
                                        </span>
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    disabled={isDebit}
                                    className="pc-method-button"
                                    onClick={() =>
                                        onPay?.({
                                            ...qrCode,
                                            inDebt: true,
                                            paymentType: "cash",
                                        })
                                    }
                                >
                                    <div className="pc-method-icon pc-method-cash">
                                        <i className="pi pi-money-bill"></i>
                                    </div>

                                    <div>
                                        <strong>
                                            {t("payment.debit") ||
                                                "Cash"}
                                        </strong>
                                        <span>
                                            {t(
                                                "payment.debitCounter"
                                            ) ||
                                                "Pay at counter or agent"}
                                        </span>
                                    </div>
                                </button>
                            </div>

                            <div className="pc-qr-box">
                                <div className="pc-qr-header">
                                    <div>
                                        <small>
                                            {t(
                                                "payment.qrPlaceholder"
                                            ) || "Scan QR code"}
                                        </small>
                                        <span>
                                            {__t("uiText.text258")}</span>
                                    </div>
                                </div>

                                <div
                                    className={`pc-qr-frame ${!hasQrCode
                                        ? "pc-qr-empty"
                                        : ""
                                        }`}
                                >
                                    {hasQrCode ? (
                                        <div className="pc-qr-inner">
                                            <QRCodeCanvas
                                                value={qrCode.value}
                                                size={
                                                    compact
                                                        ? 148
                                                        : 210
                                                }
                                                bgColor="#ffffff"
                                                fgColor="#111827"
                                                level="H"
                                                includeMargin={false}
                                            />

                                            <div className="pc-qr-logo-box">
                                                <img
                                                    src="/images/lti_logo.png"
                                                    alt="LTI"
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="pc-qr-placeholder">
                                            <i className="pi pi-qrcode"></i>
                                            <span>
                                                {__t("uiText.text259")}</span>
                                        </div>
                                    )}
                                </div>

                                {hasQrCode && (
                                    <div
                                        className={`pc-countdown ${countdown <= 10
                                            ? "pc-countdown-danger"
                                            : ""
                                            }`}
                                    >
                                        <div>
                                            <i className="pi pi-clock"></i>
                                            <span>
                                                {t(
                                                    "payment.expireIn"
                                                ) ||
                                                    "QR expires in"}
                                            </span>
                                        </div>

                                        <strong>
                                            {formatCountdown(
                                                countdown
                                            )}
                                        </strong>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
